import { useState, useEffect } from 'react';
import {
  TextField,
  Stack,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  FormControlLabel,
  Switch,
} from '@mui/material';
import * as yup from 'yup';
import { useFormik } from 'formik';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DatePicker from '@mui/lab/DatePicker';
import { format } from 'date-fns';
import axios from 'axios';

import config from '../config/config.json';
import coreTeam from '../config/coreTeam.json';
import { useLocation } from 'react-router-dom';

type PlayerListProps = {
  onDraw: (
    players: string[],
    teams: number,
    date: string,
    doNotSuffle?: boolean
  ) => void;
};

const validationSchema = yup.object({
  players: yup.string().required('Please enter players list'),
  teams: yup
    .number()
    .integer()
    .min(1, 'Minimum teams is 1')
    .max(4, 'Maximum teams is 4'),
  date: yup.date().required('Date is required'),
});

const initialValues = {
  players: '',
  teams: 4,
  date: new Date(),
};

export default function PlayerList({ onDraw }: Readonly<PlayerListProps>) {
  const [doNotRandomize, setDoNotRandomize] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const { search } = useLocation();

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: ({ players, teams, date }) => {
      if (fetchError) {
        return;
      }
      onDraw(
        players.trim().split('\n'),
        teams,
        format(date, 'dd-MM-yyyy'),
        doNotRandomize
      );
    },
  });
  const [fetching, setFetching] = useState(false);
  const [data, setData] = useState<{
    week: string;
    date: string;
    players: string[];
  } | null>(null);
  const [extraOnly, setExtraOnly] = useState(false);
  const [players, setPlayers] = useState<string[]>([]);
  const [excludeOthers, setExcludeOthers] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(search);
    const q = params.get('q');

    if (q) {
      try {
        const decoded = decodeURIComponent(atob(q));
        const bytes = Uint8Array.from(decoded, (c) => c.charCodeAt(0));
        const decodedString = new TextDecoder().decode(bytes);
        const data = JSON.parse(decodedString);
        setData(data);
        setPlayers(data.players);
        formik.setFieldValue('players', data.players.join('\n'));
      } catch (error) {
        console.error('Failed to decode players from URL', error);
      }
    }
  }, [search]); /* eslint-disable-line react-hooks/exhaustive-deps */

  const fetchPlayers = () => {
    setFetching(true);
    setFetchError(null);
    axios
      .get(config.webAppURL)
      .then((resp) => {
        if (resp.data.players.length === 0) {
          setFetchError(
            typeof resp.data.week === 'string'
              ? resp.data.week
              : 'No players found'
          );
          return;
        }
        setData(resp.data);
        setPlayers(resp.data.players);
        let players = resp.data.players;

        if (excludeOthers && formik.values.players.trim().length > 0) {
          // Remove all other players except the ones already in the text field
          const playersInField = formik.values.players.trim().split('\n');
          players = playersInField.filter((player) =>
            resp.data.players.includes(player)
          );
        }
        formik.setFieldValue('players', players.join('\n'));

        const playerCount = resp.data.players.length;

        if (playerCount <= 10) {
          formik.setFieldValue('teams', 1);
        } else if (playerCount < 18) {
          formik.setFieldValue('teams', 2);
        } else if (playerCount < 24) {
          formik.setFieldValue('teams', 3);
        } else {
          formik.setFieldValue('teams', 4);
        }

        const [date, month] = resp.data.date.split('.');
        const gameDate = new Date();
        gameDate.setDate(date);
        gameDate.setMonth(month - 1);
        formik.setFieldValue('date', gameDate);
      })
      .catch((e) => {
        console.error(e);
        setFetchError('Failed to fetch players');
      })
      .finally(() => {
        setFetching(false);
      });
  };

  const handleExtraOnlyChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!data) {
      return;
    }
    const checked = event.target.checked;
    setExtraOnly(checked);
    let players = [];
    if (checked) {
      players = data.players.filter((player: string) =>
        coreTeam.map(({ name }) => name).includes(player)
      );
      formik.setFieldValue('teams', 2);
    } else {
      players = data.players;
      formik.setFieldValue('teams', 4);
    }
    setPlayers(players);
    formik.setFieldValue('players', players.join('\n'));
  };

  const handleDoNotRandomzieChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!data) {
      return;
    }
    const checked = event.target.checked;
    setDoNotRandomize(checked);
    formik.setFieldValue('doNotRandomize', checked);
  };

  const handleExcludeAllChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = event.target.checked;
    setExcludeOthers(checked);
  };

  return (
    <Stack direction="column">
      <Typography variant="body2" sx={{ mb: 2 }}>
        Draw teams for normal weekly games
      </Typography>
      {fetchError && (
        <Typography variant="body2" color="error">
          {fetchError}
        </Typography>
      )}
      <form onSubmit={formik.handleSubmit}>
        <TextField
          id="players"
          name="players"
          label="Players List"
          multiline
          maxRows={28}
          fullWidth
          value={formik.values.players}
          onChange={formik.handleChange}
          error={formik.touched.players && Boolean(formik.errors.players)}
          helperText={formik.touched.players && formik.errors.players}
          sx={{ mb: 2, flexGrow: 1 }}
          disabled={fetching}
        />
        <Stack direction="row" alignItems="center" spacing={1}>
          <Button onClick={fetchPlayers} disabled={fetching}>
            Fetch from team sheet!
          </Button>
          {data && (
            <Typography variant="caption">
              ({`week: ${data.week} - ${data.date}, players: ${players.length}`}
              )
            </Typography>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={excludeOthers}
                onChange={handleExcludeAllChange}
              />
            }
            disabled={fetching}
            label="Exclude all but listed players"
          />
        </Stack>
        {data && (
          <>
            <FormControlLabel
              control={
                <Switch checked={extraOnly} onChange={handleExtraOnlyChange} />
              }
              disabled={fetching}
              label="Huhi extra only"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={doNotRandomize}
                  onChange={handleDoNotRandomzieChange}
                />
              }
              disabled={fetching}
              label="Do not shuffle players"
            />
          </>
        )}
        <Stack direction="row" spacing={2} sx={{ mb: 2, mt: 3 }}>
          <FormControl sx={{ flexGrow: 1 }}>
            <InputLabel>Number of teams</InputLabel>
            <Select
              id="teams"
              name="teams"
              label="Number of teams"
              value={formik.values.teams}
              onChange={formik.handleChange}
              error={formik.touched.teams && Boolean(formik.errors.teams)}
              disabled={fetching}
            >
              <MenuItem value={1}>1</MenuItem>
              <MenuItem value={2}>2</MenuItem>
              <MenuItem value={3}>3</MenuItem>
              <MenuItem value={4}>4</MenuItem>
            </Select>
          </FormControl>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            sx={{ flexGrow: 1 }}
          >
            <DatePicker
              label="Date"
              inputFormat="dd/MM/yyyy"
              value={formik.values.date}
              onChange={(value: any) => formik.setFieldValue('date', value)}
              renderInput={(params: any) => <TextField id="date" {...params} />}
              disabled={fetching}
            />
          </LocalizationProvider>
        </Stack>
        <Button variant="contained" type="submit" fullWidth disabled={fetching}>
          Draw
        </Button>
      </form>
    </Stack>
  );
}
